import React, { useCallback, useEffect, useState } from 'react';
import { ThemeProvider } from 'styled-components';
import { IntlProvider } from 'react-intl';
import { Provider } from 'react-redux';
import { styles } from '@sdir/sds';
import { NumberServiceProvider } from '@sdir/utilities/lib/providers/';
import { SdirAuthProvider } from '@sdir/auth/lib/providers';
import { SdirAuthProviderProps } from '@sdir/auth/lib/providers/SdirAuthProvider';
import store from '../store';
import { messages } from '../localization';
import { AddresseeForCurrentPersonProvider, CertificateCodesProvider } from './Providers';
import { CurrentPersonProvider } from './Providers/CurrentPersonProvider';
import { UnleashProvider } from './Providers/FeatureToggleProvider/FeatureToggleProvider';
import { CurrentRegisteredPersonProvider } from './Providers/CurrentRegisteredPersonProvider';
import { CurrentUserInfoProvider } from './Providers/CurrentUserInfoProvider';
import {
  DEFAULT_LANG,
  getCorrespondingLanguageRoute,
  getCurrentLanguageOutsideProvider,
  navigateDirectlyByUrl,
} from '../helpers/languageHelpers';
import App from '../App';

const AppProviders: React.FC = () => {
  const [language, setLanguage] = useState<string>(DEFAULT_LANG);

  const authSettings: SdirAuthProviderProps = {
    authenticationSettings: {
      client_id: process.env.REACT_APP_CLIENT_ID as string,
      client_secret: process.env.REACT_APP_CLIENT_SECRET as string,
      scope: process.env.REACT_APP_SCOPE as string,
      extraQueryParams: { securitylevel: '3' },
    },
  };

  const onChangeLanguage = useCallback(
    (newLang: string, skipRefresh?: boolean) => {
      setLanguage(newLang);
      if (!skipRefresh) {
        const newUrl = getCorrespondingLanguageRoute(language, newLang);
        setTimeout(() => {
          navigateDirectlyByUrl(newUrl);
        }, 100);
      }
    },
    [language]
  );

  useEffect(() => {
    const langFromUrl = getCurrentLanguageOutsideProvider();
    setLanguage(langFromUrl || DEFAULT_LANG);
  }, []);

  return (
    <SdirAuthProvider {...authSettings}>
      <Provider store={store}>
        <CurrentUserInfoProvider>
          <IntlProvider locale={language} messages={messages[language]}>
            <CertificateCodesProvider currentLanguage={language}>
              <UnleashProvider>
                <NumberServiceProvider>
                  <ThemeProvider theme={styles}>
                    <CurrentPersonProvider>
                      <CurrentRegisteredPersonProvider>
                        <AddresseeForCurrentPersonProvider>
                          <App currentLanguage={language} onChangeLanguage={onChangeLanguage} />
                        </AddresseeForCurrentPersonProvider>
                      </CurrentRegisteredPersonProvider>
                    </CurrentPersonProvider>
                  </ThemeProvider>
                </NumberServiceProvider>
              </UnleashProvider>
            </CertificateCodesProvider>
          </IntlProvider>
        </CurrentUserInfoProvider>
      </Provider>
    </SdirAuthProvider>
  );
};

export default AppProviders;
